import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { Overlay, Card } from '@enterprise-ui/canvas-ui-react';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import CalendarDates from './components/CalendarDates.js';
import InputField from './components/InputField.js';
import NavHeader from './components/NavHeader.js';
import { useKeyPress, toMonthArray, toActiveMonth, checkValidDateFormat, checkPastDate, checkFutureDate, checkWeekdayDate, checkWeekendDate, checkDateInList, handleDateTransition, getDate } from './utils/utility.js';

var _excluded = ["autoOk", "className", "data-testid", "disabled", "disableDates", "error", "errorText", "format", "highlight", "hintText", "id", "location", "onChange", "onError", "onUpdate", "required", "value"];
function GregorianCalendar(_ref) {
  var autoOk = _ref.autoOk;
    _ref.className;
    var dataTestId = _ref['data-testid'],
    disabled = _ref.disabled,
    disableDates = _ref.disableDates,
    error = _ref.error,
    errorText = _ref.errorText,
    _ref$format = _ref.format,
    format = _ref$format === void 0 ? 'MM/DD/YYYY' : _ref$format,
    highlight = _ref.highlight,
    hintText = _ref.hintText,
    id = _ref.id;
    _ref.location;
    _ref.onChange;
    var onError = _ref.onError,
    onUpdate = _ref.onUpdate,
    required = _ref.required,
    value = _ref.value,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    activeMonth = _useState2[0],
    setActiveMonth = _useState2[1];
  var _useState3 = useState({
      error: false,
      errorText: ''
    }),
    _useState4 = _slicedToArray(_useState3, 2),
    internalError = _useState4[0],
    setInternalError = _useState4[1];
  var externalError = {
    error: error,
    errorText: errorText
  };
  var _useState5 = useState([]),
    _useState6 = _slicedToArray(_useState5, 2),
    monthArr = _useState6[0],
    setMonthArr = _useState6[1];
  var _useState7 = useState(value),
    _useState8 = _slicedToArray(_useState7, 2),
    selectedDate = _useState8[0],
    setSelectedDate = _useState8[1];
  var _useState9 = useState(false),
    _useState10 = _slicedToArray(_useState9, 2),
    isCalendarVisible = _useState10[0],
    setIsCalendarVisible = _useState10[1];
  var onUpdateRef = useRef(onUpdate);
  var escPress = useKeyPress(27);
  useEffect(function () {
    if ((value === null || value === void 0 ? void 0 : value.length) > 0) {
      setSelectedDate(value.trim());
    } else {
      setSelectedDate(value ? '' : null);
    }
  }, [value]);
  useEffect(function () {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);
  useEffect(function () {
    escPress && setIsCalendarVisible(false);
  }, [escPress]);
  useEffect(function () {
    setMonthArr(isCalendarVisible ? toMonthArray(activeMonth, disableDates, format) : []);
  }, [activeMonth, isCalendarVisible, disableDates, format]);
  useEffect(function () {
    var date = internalError.error ? '' : selectedDate;
    setActiveMonth(isCalendarVisible ? toActiveMonth(date) : '');
  }, [internalError.error, selectedDate, isCalendarVisible]);
  var checkError = useCallback(function (dateIn) {
    if (dateIn && !checkValidDateFormat(dateIn, format)) {
      setInternalError({
        error: true,
        errorText: "Please enter a valid date with format of ".concat(format)
      });
      return true;
    } else if (disableDates && disableDates.past && checkPastDate(dateIn)) {
      setInternalError({
        error: true,
        errorText: 'Please select future date'
      });
      return true;
    } else if (disableDates !== null && disableDates !== void 0 && disableDates.before && checkPastDate(dateIn, disableDates.before, format)) {
      setInternalError({
        error: true,
        errorText: "Please select future date from ".concat(disableDates.before)
      });
      return true;
    } else if (disableDates && disableDates.future && checkFutureDate(dateIn)) {
      setInternalError({
        error: true,
        errorText: 'Please select past date'
      });
      return true;
    } else if (disableDates && disableDates.weekdays && checkWeekdayDate(dateIn, format)) {
      setInternalError({
        error: true,
        errorText: 'Please select a weekend date'
      });
      return true;
    } else if (disableDates && disableDates.weekends && checkWeekendDate(dateIn, format)) {
      setInternalError({
        error: true,
        errorText: 'Please select a weekday date'
      });
      return true;
    } else if (dateIn && disableDates && disableDates.except && !checkDateInList(dateIn, disableDates.except)) {
      setInternalError({
        error: true,
        errorText: 'Please select valid date'
      });
    } else if (disableDates && disableDates.only && checkDateInList(dateIn, disableDates.only)) {
      setInternalError({
        error: true,
        errorText: 'Please select valid date'
      });
    } else {
      setInternalError({
        error: false,
        errorText: ''
      });
      return false;
    }
  }, [disableDates, format]);
  useEffect(function () {
    var isComponentErrored = checkError(selectedDate);
    if (isComponentErrored) {
      onError && onError(true);
    }
    if (!isComponentErrored && onUpdateRef.current && selectedDate !== undefined && selectedDate !== null) {
      onError && onError(false);
      onUpdateRef.current(id, selectedDate);
    }
  }, [selectedDate, checkError, id, onError]);
  var selectMonth = useCallback(function (type) {
    setActiveMonth(toActiveMonth(activeMonth ? activeMonth : selectedDate, type));
  }, [activeMonth, selectedDate]);
  var selectDate = useCallback(function (type) {
    setSelectedDate(handleDateTransition(selectedDate, type, disableDates, format));
    setIsCalendarVisible(false);
  }, [selectedDate, disableDates]);
  var changeEvent = useCallback(function (event) {
    setSelectedDate(getDate(event.target.value));
  }, []);
  if (disableDates !== null && disableDates !== void 0 && disableDates.except && (disableDates.only || disableDates.weekdays || disableDates.weekends || disableDates.past || disableDates.future || disableDates.before)) {
    throw new Error('Your datepicker disabled date rules have a conflict. "Except" cannot be combined with other rules.');
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Overlay, {
    isVisible: isCalendarVisible,
    onClick: function onClick() {
      return setIsCalendarVisible(false);
    }
  }), /*#__PURE__*/React.createElement(InputField, _extends({}, otherProps, {
    id: id,
    changeEvent: changeEvent,
    required: required,
    "data-testid": dataTestId,
    disabled: !!disabled //not undefined only true/false
    ,
    format: format,
    hintText: hintText,
    highlight: highlight,
    internalError: internalError,
    externalError: externalError,
    disableDates: disableDates,
    selectDate: selectDate,
    selectedDate: selectedDate,
    isCalendarVisible: isCalendarVisible,
    setIsCalendarVisible: setIsCalendarVisible,
    onClear: function onClear() {
      return setSelectedDate('');
    }
  })), isCalendarVisible && !disabled && /*#__PURE__*/React.createElement(Card, {
    elevation: 2,
    className: "C-DatePicker__Card"
  }, /*#__PURE__*/React.createElement(NavHeader, {
    activeMonth: activeMonth,
    selectMonth: selectMonth
  }), /*#__PURE__*/React.createElement(CalendarDates, {
    autoOk: autoOk,
    disableDates: disableDates,
    format: format,
    monthArr: monthArr,
    selectedDate: selectedDate,
    setDate: setSelectedDate,
    setIsCalendarVisible: setIsCalendarVisible
  })));
}
var DatePicker = /*#__PURE__*/React.memo(GregorianCalendar);

export { DatePicker as default };
